const OPTION_SIGHT_ITEM_PROPERTY_PREFIX = '_optionsight_item_';

function getOptionSightLineProperties(item) {
  return item?.properties && typeof item.properties === 'object' ? item.properties : {};
}

function getOptionSightDatasetValue(item, key) {
  return String(item?.dataset?.[key] ?? '').trim();
}

function isOptionSightAddonLinkedToParent(parentItem, addonItem) {
  const parentGroupId = getOptionSightDatasetValue(parentItem, 'optionsightGroupId');
  const addonGroupId = getOptionSightDatasetValue(addonItem, 'optionsightGroupId');
  const addonParentId = getOptionSightDatasetValue(addonItem, 'optionsightParentId');

  // Current OptionSight carts use a unique group ID for every configured
  // parent/add-on set. Once either side has current group metadata, never fall
  // back to the shared parent variant ID: two separately configured copies of
  // the same product intentionally have the same variant ID.
  if (parentGroupId || addonGroupId) {
    return Boolean(parentGroupId && (addonParentId === parentGroupId || addonGroupId === parentGroupId));
  }

  // Historical carts created before group IDs linked add-ons only through the
  // parent variant. Keep that compatibility path strictly legacy-only.
  const parentVariantId = getOptionSightDatasetValue(parentItem, 'optionsightParentVariantId');
  const addonParentVariantId = getOptionSightDatasetValue(addonItem, 'optionsightParentVariantId');
  return Boolean(
    parentVariantId && (addonParentId === parentVariantId || addonParentVariantId === parentVariantId)
  );
}

function buildOptionSightGroupUpdates(parentItem, addonItems, quantity) {
  const parentKey = getOptionSightDatasetValue(parentItem, 'cartItemKey');
  if (getOptionSightDatasetValue(parentItem, 'optionsightRole') !== 'parent' || !parentKey) return null;

  const updates = { [parentKey]: Number(quantity) };
  Array.from(addonItems || []).forEach((addonItem) => {
    const addonKey = getOptionSightDatasetValue(addonItem, 'cartItemKey');
    if (addonKey && isOptionSightAddonLinkedToParent(parentItem, addonItem)) {
      updates[addonKey] = Number(quantity);
    }
  });

  return updates;
}

function buildOptionSightOrphanUpdates(parentItems, addonItems) {
  const parents = Array.from(parentItems || []);
  const updates = {};

  Array.from(addonItems || []).forEach((addonItem) => {
    const addonKey = getOptionSightDatasetValue(addonItem, 'cartItemKey');
    if (addonKey && !parents.some((parentItem) => isOptionSightAddonLinkedToParent(parentItem, addonItem))) {
      updates[addonKey] = 0;
    }
  });

  return updates;
}

function parseOptionSightNonNegativeInteger(value) {
  const normalized = String(value ?? '').trim();
  if (!/^(0|[1-9]\d*)$/.test(normalized)) return null;
  const number = Number(normalized);
  return Number.isSafeInteger(number) && number >= 0 ? number : null;
}

function parseOptionSightExpectedItems(properties, expectedCount) {
  const entries = Object.entries(properties)
    .filter(([key]) => key.startsWith(OPTION_SIGHT_ITEM_PROPERTY_PREFIX))
    .map(([key, value]) => {
      const index = parseOptionSightNonNegativeInteger(key.slice(OPTION_SIGHT_ITEM_PROPERTY_PREFIX.length));
      const match = String(value ?? '')
        .trim()
        .match(/^([1-9]\d*)@([1-9]\d*)@(0|[1-9]\d*)$/);
      if (!index || !match) return null;
      const units = parseOptionSightNonNegativeInteger(match[2]);
      const priceMinor = parseOptionSightNonNegativeInteger(match[3]);
      if (!units || priceMinor === null) return null;
      return { index, variantId: match[1], units, priceMinor };
    });

  if (entries.some((entry) => !entry) || entries.length !== expectedCount) return null;
  entries.sort((left, right) => left.index - right.index);
  if (entries.some((entry, index) => entry.index !== index + 1)) return null;

  const variantIds = new Set(entries.map((entry) => entry.variantId));
  if (variantIds.size !== entries.length) return null;
  for (let index = 1; index < entries.length; index += 1) {
    if (BigInt(entries[index - 1].variantId) >= BigInt(entries[index].variantId)) return null;
  }

  return entries;
}

function validateOptionSightCartIntegrity(cart) {
  if (!cart || !Array.isArray(cart.items)) return { valid: false, issue: 'cart_unavailable' };

  if (cart.items.length > 0) {
    const totalPrice = Number.isFinite(Number(cart.total_price))
      ? Number(cart.total_price)
      : cart.items.reduce(
          (sum, item) => sum + Number(item.final_line_price ?? item.original_line_price ?? 0),
          0
        );
    if (totalPrice <= 0) return { valid: false, issue: 'non_positive_total' };
  }

  const lines = cart.items.map((item) => {
    const properties = getOptionSightLineProperties(item);
    return {
      item,
      properties,
      groupId: String(properties._optionsight_group_id ?? '').trim(),
      forGroupId: String(properties._optionsight_for ?? '').trim(),
      parentVariantId: String(properties._optionsight_parent_variant_id ?? '').trim(),
      variantId: String(item.variant_id ?? item.id ?? '').trim(),
    };
  });

  const children = lines.filter((line) => line.forGroupId || line.parentVariantId);
  const parents = lines.filter((line) => {
    if (line.forGroupId) return false;
    const hasExpectedItem = Object.keys(line.properties).some((key) =>
      key.startsWith(OPTION_SIGHT_ITEM_PROPERTY_PREFIX)
    );
    return Boolean(
      line.groupId ||
        line.properties['Selected custom options'] ||
        line.properties._optionsight_integrity ||
        line.properties._optionsight_expected_count !== undefined ||
        line.properties._optionsight_expected_amount !== undefined ||
        hasExpectedItem
    );
  });

  for (const child of children) {
    if (!child.forGroupId || !child.groupId || !child.parentVariantId) {
      return { valid: false, issue: 'orphan_child' };
    }
    if (child.groupId !== child.forGroupId) return { valid: false, issue: 'mixed_group' };
    const matchingParents = parents.filter(
      (parent) => parent.groupId === child.forGroupId && parent.variantId === child.parentVariantId
    );
    if (matchingParents.length !== 1) return { valid: false, issue: 'orphan_child' };
  }

  for (const parent of parents) {
    if (!parent.groupId || String(parent.properties._optionsight_integrity ?? '') !== 'v1') {
      return { valid: false, issue: 'missing_parent_metadata' };
    }

    const expectedCount = parseOptionSightNonNegativeInteger(parent.properties._optionsight_expected_count);
    const expectedAmount = parseOptionSightNonNegativeInteger(parent.properties._optionsight_expected_amount);
    if (expectedCount === null || expectedAmount === null) {
      return { valid: false, issue: 'invalid_parent_metadata' };
    }

    if (parents.filter((candidate) => candidate.groupId === parent.groupId).length !== 1) {
      return { valid: false, issue: 'duplicate_parent_group' };
    }

    const expectedItems = parseOptionSightExpectedItems(parent.properties, expectedCount);
    if (!expectedItems) return { valid: false, issue: 'invalid_item_metadata' };

    const declaredAmount = expectedItems.reduce((sum, expected) => sum + expected.units * expected.priceMinor, 0);
    if (declaredAmount !== expectedAmount) return { valid: false, issue: 'expected_totals_mismatch' };

    const groupChildren = children.filter((child) => child.forGroupId === parent.groupId);
    if (groupChildren.length !== expectedCount) return { valid: false, issue: 'missing_or_duplicate_child' };

    for (const expected of expectedItems) {
      const matchingChildren = groupChildren.filter(
        (child) =>
          child.groupId === parent.groupId &&
          child.parentVariantId === parent.variantId &&
          child.variantId === expected.variantId
      );
      if (matchingChildren.length !== 1) return { valid: false, issue: 'missing_or_duplicate_child' };

      const childItem = matchingChildren[0].item;
      const expectedQuantity = Number(parent.item.quantity) * expected.units;
      if (Number(childItem.quantity) !== expectedQuantity || Number(childItem.original_price) !== expected.priceMinor) {
        return { valid: false, issue: 'child_quantity_or_price_mismatch' };
      }
    }

    const actualQuantity = groupChildren.reduce((sum, child) => sum + Number(child.item.quantity), 0);
    const actualAmount = groupChildren.reduce((sum, child) => sum + Number(child.item.original_line_price), 0);
    const expectedQuantity =
      expectedItems.reduce((sum, expected) => sum + expected.units, 0) * Number(parent.item.quantity);
    const expectedGroupAmount = expectedAmount * Number(parent.item.quantity);
    if (actualQuantity !== expectedQuantity || actualAmount !== expectedGroupAmount) {
      return { valid: false, issue: 'group_totals_mismatch' };
    }
  }

  return { valid: true, issue: 'valid' };
}

function getOptionSightCheckoutIntegrityMarker(event) {
  const candidates = [event?.submitter, ...(event?.composedPath?.() || [])];

  for (const candidate of candidates) {
    if (!(candidate instanceof Element)) continue;
    const marker = candidate.matches('[data-optionsight-cart-integrity]')
      ? candidate
      : candidate.closest('[data-optionsight-cart-integrity]');
    if (marker) return marker;
  }

  return null;
}

function hasInvalidOptionSightCartMarker(event) {
  const marker = getOptionSightCheckoutIntegrityMarker(event);
  return !marker || marker.dataset.optionsightCartIntegrity !== 'valid';
}

function focusOptionSightIntegrityWarning() {
  const warning = document.querySelector('[data-optionsight-integrity-warning]');
  warning?.focus({ preventScroll: true });
  warning?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function applyPendingOptionSightCartIntegrity() {
  document.querySelectorAll('[data-optionsight-cart-integrity]').forEach((marker) => {
    marker.dataset.optionsightCartIntegrity = 'verification_pending';
  });

  document.querySelectorAll('[data-optionsight-checkout-entry]').forEach((entry) => {
    if (entry instanceof HTMLButtonElement || entry instanceof HTMLInputElement) {
      entry.disabled = true;
      entry.setAttribute('aria-disabled', 'true');
      entry.dataset.optionsightIntegrityDisabled = 'true';
    } else {
      entry.hidden = true;
      entry.dataset.optionsightIntegrityHidden = 'true';
    }
  });

  document.querySelectorAll('.cart__express-checkout, .cart__dynamic-checkout-buttons').forEach((entry) => {
    entry.hidden = true;
    entry.dataset.optionsightIntegrityHidden = 'true';
  });
}

function applyInvalidOptionSightCartIntegrity(issue) {
  const normalizedIssue = issue || 'invalid';
  document.querySelectorAll('[data-optionsight-cart-integrity]').forEach((marker) => {
    marker.dataset.optionsightCartIntegrity = normalizedIssue;
  });

  document.querySelectorAll('[data-optionsight-checkout-entry]').forEach((entry) => {
    if (entry instanceof HTMLButtonElement || entry instanceof HTMLInputElement) {
      entry.disabled = true;
      entry.setAttribute('aria-disabled', 'true');
      entry.dataset.optionsightIntegrityDisabled = 'true';
    } else {
      entry.hidden = true;
      entry.dataset.optionsightIntegrityHidden = 'true';
    }
  });

  document.querySelectorAll('.cart__express-checkout, .cart__dynamic-checkout-buttons').forEach((entry) => {
    entry.hidden = true;
    entry.dataset.optionsightIntegrityHidden = 'true';
  });

  if (!document.querySelector('[data-optionsight-integrity-warning]')) {
    const checkoutEntry = document.querySelector('[data-optionsight-checkout-entry]');
    if (checkoutEntry) {
      const warning = document.createElement('div');
      warning.className = 'auxiliary-purchase-warning';
      warning.role = 'alert';
      warning.tabIndex = -1;
      warning.dataset.optionsightIntegrityWarning = '';
      warning.dataset.optionsightIntegrityIssue = normalizedIssue;
      warning.innerHTML =
        '<strong>Checkout paused.</strong> We couldn’t verify all selected custom options and charges. ' +
        'Refresh this page to verify the cart again. If the message remains, remove the affected custom product, ' +
        'add it again, and reselect its options so every item and price is included.';
      checkoutEntry.before(warning);
    }
  }

  if (window.optionSightLastCartIntegrityIssue !== normalizedIssue) {
    window.optionSightLastCartIntegrityIssue = normalizedIssue;
    document.dispatchEvent(
      new CustomEvent('optionsight:checkout-integrity', {
        detail: { stage: 'cart-guard', status: 'blocked', reason: normalizedIssue },
      })
    );
    window.dataLayer?.push?.({
      event: 'optionsight_checkout_integrity',
      stage: 'cart-guard',
      status: 'blocked',
      reason: normalizedIssue,
    });
  }
}

function applyValidOptionSightCartIntegrity({ enableCheckout = true } = {}) {
  window.optionSightLastCartIntegrityIssue = null;
  document.querySelectorAll('[data-optionsight-cart-integrity]').forEach((marker) => {
    marker.dataset.optionsightCartIntegrity = 'valid';
  });
  document.querySelectorAll('[data-optionsight-integrity-warning]').forEach((warning) => warning.remove());

  if (!enableCheckout) return;
  document.querySelectorAll('[data-optionsight-integrity-disabled="true"]').forEach((entry) => {
    entry.disabled = false;
    entry.removeAttribute('aria-disabled');
    delete entry.dataset.optionsightIntegrityDisabled;
  });
  document.querySelectorAll('[data-optionsight-integrity-hidden="true"]').forEach((entry) => {
    entry.hidden = false;
    delete entry.dataset.optionsightIntegrityHidden;
  });
}

function syncOptionSightCartIntegrityMarkerFromSection(sectionHtml) {
  if (!sectionHtml) return null;
  const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const source = html.querySelector('[data-optionsight-cart-integrity]');
  if (!source) return null;

  const status = source.dataset.optionsightCartIntegrity || 'invalid';
  let target = null;
  if (source.id) target = document.getElementById(source.id);
  if (!target && source.matches('cart-drawer')) target = document.querySelector('cart-drawer');
  if (!target && source.matches('cart-notification')) target = document.querySelector('cart-notification');
  if (target) target.dataset.optionsightCartIntegrity = status;
  return status;
}

async function verifyOptionSightCartNotificationIntegrity(verificationId) {
  const cartUrl = window.routes?.cart_url || '/cart';
  const sectionSeparator = cartUrl.includes('?') ? '&' : '?';
  const [cartResponse, sectionResponse] = await Promise.all([
    fetch(`${cartUrl}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }),
    // Shopify content-negotiates /cart: a JSON Accept header returns cart JSON instead of the requested section map.
    fetch(`${cartUrl}${sectionSeparator}sections=main-cart-footer`, {
      cache: 'no-store',
    }),
  ]);

  if (!cartResponse.ok || !sectionResponse.ok) throw new Error('OptionSight cart verification request failed');

  const [cartState, sectionPayload] = await Promise.all([cartResponse.json(), sectionResponse.json()]);
  if (verificationId !== window.optionSightCartNotificationVerificationId) return;

  const jsonIntegrity = validateOptionSightCartIntegrity(cartState);
  const sectionHtml =
    sectionPayload && typeof sectionPayload === 'object' && !Array.isArray(sectionPayload)
      ? sectionPayload['main-cart-footer']
      : null;
  const serverStatus =
    typeof sectionHtml === 'string' ? syncOptionSightCartIntegrityMarkerFromSection(sectionHtml) : null;

  if (!serverStatus) {
    applyInvalidOptionSightCartIntegrity('server_marker_unavailable');
  } else if (serverStatus !== 'valid') {
    applyInvalidOptionSightCartIntegrity(serverStatus);
  } else if (!jsonIntegrity.valid) {
    applyInvalidOptionSightCartIntegrity(jsonIntegrity.issue);
  } else {
    applyValidOptionSightCartIntegrity({ enableCheckout: Number(cartState.item_count) > 0 });
  }
}

function requestOptionSightCartIntegrityVerification() {
  const verificationId = (window.optionSightCartNotificationVerificationId || 0) + 1;
  window.optionSightCartNotificationVerificationId = verificationId;
  applyPendingOptionSightCartIntegrity();
  verifyOptionSightCartNotificationIntegrity(verificationId).catch(() => {
    if (verificationId !== window.optionSightCartNotificationVerificationId) return;
    applyInvalidOptionSightCartIntegrity('cart_verification_failed');
  });
  return verificationId;
}

function installOptionSightCartNotificationIntegrity() {
  const registry = window.customElements;
  const CartNotificationElement = registry?.get?.('cart-notification');
  if (!CartNotificationElement) {
    registry?.whenDefined?.('cart-notification').then(installOptionSightCartNotificationIntegrity);
    return;
  }

  const prototype = CartNotificationElement.prototype;
  if (prototype.optionSightIntegrityInstalled) return;

  const renderContents = prototype.renderContents;
  if (typeof renderContents !== 'function') return;
  prototype.optionSightIntegrityInstalled = true;
  prototype.renderContents = function renderOptionSightVerifiedContents(parsedState) {
    requestOptionSightCartIntegrityVerification();
    const result = renderContents.call(this, parsedState);
    return result;
  };
}

function isOptionSightCheckoutEvent(event) {
  if (event.type === 'submit') {
    return ['cart', 'CartDrawer-Form', 'cart-notification-form'].includes(event.target?.id);
  }

  return event.composedPath().some(
    (element) =>
      element instanceof Element &&
      (element.matches('[data-optionsight-checkout-entry]') ||
        element.matches('.cart__dynamic-checkout-buttons') ||
        element.matches('#checkout, #CartDrawer-Checkout, #cart-notification-form [name="checkout"]') ||
        element.matches('shopify-accelerated-checkout-cart'))
  );
}

function guardInvalidOptionSightCheckout(event) {
  if (!isOptionSightCheckoutEvent(event) || !hasInvalidOptionSightCartMarker(event)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (!getOptionSightCheckoutIntegrityMarker(event)) {
    applyInvalidOptionSightCartIntegrity('missing_cart_marker');
  }
  focusOptionSightIntegrityWarning();
}

if (!window.optionSightCartIntegrityGuardInstalled) {
  window.optionSightCartIntegrityGuardInstalled = true;
  document.addEventListener('click', guardInvalidOptionSightCheckout, true);
  document.addEventListener('submit', guardInvalidOptionSightCheckout, true);
}

installOptionSightCartNotificationIntegrity();

window.validateOptionSightCartIntegrity = validateOptionSightCartIntegrity;
window.applyInvalidOptionSightCartIntegrity = applyInvalidOptionSightCartIntegrity;
